import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { useAuth } from "../auth/AuthProvider";
import { apolloClient } from "../apollo/client";

const ERROR_MESSAGES: Record<number, string> = {
  410: "This impersonation link has expired. Generate a new one from Reflex Ops.",
  409: "This impersonation link has already been used. Generate a new one from Reflex Ops.",
  400: "Invalid impersonation link.",
  403: "This impersonation link is no longer valid.",
};

export function Impersonate() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { refresh } = useAuth();

  const [error, setError] = useState<string | null>(null);
  const redeeming = useRef(false);

  const token = searchParams.get("token");

  useEffect(() => {
    if (!token) {
      navigate("/login", { replace: true });
      return;
    }

    // Guard against StrictMode double-mount and re-renders.
    if (redeeming.current) return;
    redeeming.current = true;

    async function redeem() {
      try {
        const response = await fetch("/auth/impersonate/redeem", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({ token }),
        });

        if (!response.ok) {
          const message =
            ERROR_MESSAGES[response.status] ??
            "Something went wrong. Please try again.";
          setError(message);
          return;
        }

        // Session is now established. Refresh the AuthProvider so the rest
        // of the app sees the impersonated user, then clear any stale cache
        // from a previous session before navigating home.
        await refresh();
        try {
          await apolloClient.resetStore();
        } catch {
          // resetStore() rejects if a refetched query errors; ignore so the
          // handoff still completes.
        }
        navigate("/", { replace: true });
      } catch {
        setError("Something went wrong. Please try again.");
      }
    }

    void redeem();
  }, [token, navigate, refresh]);

  return (
    <div className="relative min-h-screen overflow-hidden bg-paper text-ink antialiased pt-[var(--banner-h)]">
      <div
        aria-hidden
        className="pointer-events-none absolute -left-[140px] -top-[200px] h-[520px] w-[520px] rounded-full bg-tide/10 blur-[80px]"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-[240px] -right-[120px] h-[460px] w-[460px] rounded-full bg-spark/10 blur-[80px]"
      />

      <nav className="relative z-20 flex items-center justify-between px-8 pt-7 md:px-14">
        <div className="flex items-center gap-2.5">
          <img
            src="/reflex-mark.svg"
            alt=""
            width="32"
            height="32"
            className="h-8 w-8"
          />
          <span className="text-[15px] font-semibold tracking-tight">
            Reflex Labs
          </span>
        </div>
      </nav>

      <main className="relative z-10 flex min-h-[calc(100vh-140px)] items-center justify-center px-6 py-12">
        <div className="w-full max-w-[400px] text-center" aria-live="polite">
          {error ? (
            <div className="portal-reveal portal-reveal-1">
              <div className="mb-4">
                <div className="mx-auto mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-rust-s">
                  <svg
                    aria-hidden="true"
                    width="20"
                    height="20"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    className="text-rust"
                  >
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <h1 className="font-display text-[28px] font-medium leading-tight tracking-tight">
                  Impersonation failed
                </h1>
              </div>
              <div className="login-card-shadow rounded-2xl bg-paper-hi p-8">
                <p className="text-[14px] leading-relaxed text-ink-3">
                  {error}
                </p>
              </div>
            </div>
          ) : (
            <div className="portal-reveal portal-reveal-1">
              <div className="mb-6">
                <div className="mx-auto mb-5 h-8 w-8 animate-spin rounded-full border-2 border-tide border-t-transparent" />
                <h1 className="font-display text-[28px] font-medium leading-tight tracking-tight">
                  Establishing session...
                </h1>
                <p className="mt-3 text-[14px] text-ink-4">
                  Setting up your impersonation session.
                </p>
              </div>
            </div>
          )}
        </div>
      </main>

      <footer className="relative z-10 flex items-center justify-between px-8 py-6 text-[11.5px] text-ink-4 md:px-14">
        <span>Reflex Labs</span>
      </footer>
    </div>
  );
}
